import { forwardRef, type InputHTMLAttributes } from 'react';
import { cn } from '@/lib/utils';

export interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  error?: string;
  helperText?: string;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
  compact?: boolean;
}

const Input = forwardRef<HTMLInputElement, InputProps>(
  ({ className, label, error, helperText, leftIcon, rightIcon, id, compact = false, ...props }, ref) => {
    const inputId = id || label?.toLowerCase().replace(/\s+/g, '-');

    return (
      <div className="w-full">
        {label && (
          <label
            htmlFor={inputId}
            className={cn(
              'block font-medium',
              'text-gray-700 dark:text-secondary-300',
              compact ? 'mb-1 text-xs' : 'mb-1.5 text-sm'
            )}
          >
            {label}
          </label>
        )}
        <div className="relative">
          {leftIcon && (
            <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3 text-gray-400 dark:text-secondary-500">
              {leftIcon}
            </div>
          )}
          <input
            ref={ref}
            id={inputId}
            className={cn(
              'flex w-full rounded-lg border text-sm transition-all duration-200',
              'placeholder:text-gray-400 dark:placeholder:text-secondary-500',
              'focus:outline-none focus:ring-2 focus:ring-offset-0',
              'disabled:cursor-not-allowed disabled:opacity-50',
              // Compact size
              compact ? 'h-8 px-2.5 py-1.5' : 'h-9 px-3 py-2',
              // Light mode
              'bg-white border-gray-300',
              'focus:border-primary-500 focus:ring-primary-500/20',
              'disabled:bg-gray-50',
              // Dark mode
              'dark:bg-secondary-900/50 dark:border-white/[0.1]',
              'dark:text-secondary-100',
              'dark:focus:border-primary-500/50 dark:focus:ring-primary-500/20',
              'dark:focus:bg-secondary-900/80',
              'dark:disabled:bg-secondary-900/30',
              // Error state
              error && cn(
                'border-danger-500 focus:border-danger-500 focus:ring-danger-500/20',
                'dark:border-danger-500/50 dark:focus:border-danger-500/70'
              ),
              // Icon padding
              leftIcon && 'pl-9',
              rightIcon && 'pr-9',
              className
            )}
            {...props}
          />
          {rightIcon && (
            <div className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-400 dark:text-secondary-500">
              {rightIcon}
            </div>
          )}
        </div>
        {error && (
          <p className={cn('text-danger-600 dark:text-danger-400', compact ? 'mt-1 text-xs' : 'mt-1.5 text-sm')}>
            {error}
          </p>
        )}
        {helperText && !error && (
          <p className={cn('text-gray-500 dark:text-secondary-500', compact ? 'mt-1 text-xs' : 'mt-1.5 text-sm')}>
            {helperText}
          </p>
        )}
      </div>
    );
  }
);

Input.displayName = 'Input';

export { Input };
